import React from 'react';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import styled from 'styled-components';
import Select, { components } from 'react-select';
import { Contact, ContactType, ContactAccessLevel } from 'app/console-api/apps/apps';
import {
  DropdownStyles,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import DeleteBinIcon from 'assets/icons/ic-delete-bin.svg';

const StyledEmptyListBlock = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    minHeight: '92px',
    borderRadius: '12px',
    marginTop: '8px',
    marginBottom: '8px',
    backgroundColor: theme.styleguideColors.fillSecondary,
    alignItems: 'center',
    justifyContent: 'center'
  })
);

const StyledContactControlWrap = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    gap: '4px',
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid',
    padding: '8px',
    borderColor: theme.styleguideColors.borderPrimary,
    borderRadius: '12px'
  })
);

const StyledAddControlButton = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '48px',
    color: theme.styleguideColors.contentBlue,
    '&:hover': {
      color: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

const NoAutoCompleteInput = (props: any) => <components.Input {...props} autoComplete="off" />;

const ContactTypeOptions = [
  { value: ContactType.Email, label: 'Email' },
  { value: ContactType.Link, label: 'Link' },
  { value: ContactType.Phone, label: 'Phone' }
];

const ContactAccessOptions = [
  { value: ContactAccessLevel.Public, label: 'Public' },
  { value: ContactAccessLevel.PartnersOnly, label: 'Partners Only' }
];

interface AppContactsInputProps {
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
}

const ControlDropdownStyles = () => DropdownStyles({ controlMinWidth: '120px', menuPosition: 'absolute' });


export const AppContactsInput: React.FC<AppContactsInputProps> = ({ contacts, onChange }) => {
  const handleAddContact = () => {
    onChange([
      ...contacts,
      {
        type: ContactType.Link,
        value: '',
        access: ContactAccessLevel.Public
      }
    ]);
  };

  const handleRemoveContact = (index: number) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    onChange(newContacts);
  };

  const handleContactChange = (index: number, field: keyof Contact, value: any) => {
    const newContacts = [...contacts];
    newContacts[index] = {
      ...newContacts[index],
      [field]: value
    };
    onChange(newContacts);
  };

  return (
    <FlexColumn style={{ width: '100%' }}>
      <BodyText size={1} scale="sm">
        Contacts
      </BodyText>
      <BodyText size={3} scale={'sm'} variation={'darkGray'} style={{ marginTop: '4px', marginBottom: '8px' }}>
        Public contacts are visible to everyone, while Partners Only contacts are shared when a deal is requested or approved.
      </BodyText>

      {contacts.length ? (
        contacts.map((contact, index) => {
          return (
            <StyledContactControlWrap key={index}>
              <FlexColumn style={{ flex: 4 }} gap={10}>
                <FlexRow gap={10}>
                  <div style={{ flex: 1 }}>
                    <Select
                      value={ContactTypeOptions.find((opt) => opt.value === contact.type)}
                      onChange={(e) => handleContactChange(index, 'type', e?.value)}
                      options={ContactTypeOptions}
                      styles={ControlDropdownStyles()}
                      name={`contact-type-${index}`}
                      inputId={`contact-type-${index}`}
                      isSearchable={false}
                      components={{ Input: NoAutoCompleteInput }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Select
                      value={ContactAccessOptions.find((opt) => opt.value === contact.access)}
                      onChange={(e) => handleContactChange(index, 'access', e?.value)}
                      options={ContactAccessOptions}
                      styles={ControlDropdownStyles()}
                      isSearchable={false}
                      components={{ Input: NoAutoCompleteInput }}
                    />
                  </div>
                </FlexRow>
                <StyledInput
                  placeholder={
                    contact.type === ContactType.Email
                      ? 'email@example.com'
                      : contact.type === ContactType.Phone
                        ? '+1234567890'
                        : 'https://example.com'
                  }
                  value={contact.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleContactChange(index, 'value', e.target.value)
                  }
                />
              </FlexColumn>
              <FlexColumn style={{ flex: 1, width: '36px', alignItems: 'center' }}>
                <StyledDismissButton
                  color={'utility'}
                  height={'36'}
                  onClick={() => handleRemoveContact(index)}
                  style={{ width: '36px' }}>
                  <SvgIcon src={DeleteBinIcon} size={16} />
                </StyledDismissButton>
              </FlexColumn>
            </StyledContactControlWrap>
          );
        })
      ) : (
        <StyledEmptyListBlock>No contacts yet</StyledEmptyListBlock>
      )}

      <FlexRow>
        <StyledAddControlButton
          size={3}
          scale={'sm'}
          variation={'blue'}
          onClick={handleAddContact}
        >
          + Add contact
        </StyledAddControlButton>
      </FlexRow>
    </FlexColumn>
  );
};
